// REQ-P1-06b: イベントチャット画面の契約型付きAPI。

import { api, type paths } from './client';
import { ApiStatusError } from './templates';

type EventChatMessagesResponse = NonNullable<
  paths['/event-chat/events/{eventId}/messages/{offset}/{limit}']['get']['responses'][200]['content']
>['application/json'];
type EventChatMessagesWindowResponse = NonNullable<
  paths['/event-chat/events/{eventId}/messages/around/{messageId}/{before}/{after}']['get']['responses'][200]['content']
>['application/json'];
type EventChatNewMessagesResponse = NonNullable<
  paths['/event-chat/events/{eventId}/messages/new/{lastCreatedAt}']['get']['responses'][200]['content']
>['application/json'];
type AdminEventChatUnreadCountsResponse = NonNullable<
  paths['/event-chat/unread-counts/admin']['get']['responses'][200]['content']
>['application/json'];
type ArtistEventChatUnreadCountsResponse = NonNullable<
  paths['/event-chat/unread-counts/artist']['get']['responses'][200]['content']
>['application/json'];
type ArtistEventChatAvailableEventsResponse = NonNullable<
  paths['/artist/event-chat/available-events/{offset}']['get']['responses'][200]['content']
>['application/json'];
type EventChatReadRequest = NonNullable<
  paths['/event-chat/read']['post']['requestBody']
>['content']['application/json'];
type AdminEventChatMessageReadsResponse = NonNullable<
  paths['/admin/event-chat/events/{eventId}/reads/{messageId}']['get']['responses'][200]['content']
>['application/json'];
type AdminEventChatParticipantsResponse = NonNullable<
  paths['/admin/event-chat/participants/{eventId}']['get']['responses'][200]['content']
>['application/json'];
type ArtistEventChatParticipantsResponse = NonNullable<
  paths['/artist/event-chat/participants/{eventId}']['get']['responses'][200]['content']
>['application/json'];
type EventChatTextMessageRequest = NonNullable<
  paths['/events/{eventId}/messages/text']['post']['requestBody']
>['content']['application/json'];
type EventChatImageUploadRequest = NonNullable<
  paths['/events/{eventId}/messages/images']['post']['requestBody']
>['content']['multipart/form-data'];
type EventChatFileUploadRequest = NonNullable<
  paths['/events/{eventId}/messages/files']['post']['requestBody']
>['content']['multipart/form-data'];
type AdminEventChatSidebarRowResponse = NonNullable<
  paths['/admin/event-chat/sidebar-row/{eventId}']['get']['responses'][200]['content']
>['application/json'];
type AdminEventChatNotifyMuteResponse = NonNullable<
  paths['/admin/event-chat/notify-mute/{eventId}']['get']['responses'][200]['content']
>['application/json'];
type UpdateAdminEventChatNotifyMuteRequest = NonNullable<
  paths['/admin/event-chat/notify-mute']['post']['requestBody']
>['content']['application/json'];
type CastingArtistsQuery = NonNullable<
  paths['/admin/casting-artists']['get']['parameters']['query']
>;
type CastingArtistsResponse = NonNullable<
  paths['/admin/casting-artists']['get']['responses'][200]['content']
>['application/json'];
type AddAdminEventChatManualArtistsRequest = NonNullable<
  paths['/admin/event-chat/participants/{eventId}/manual-artists']['post']['requestBody']
>['content']['application/json'];
type AdminEventChatParticipantsSnapshotResponse = NonNullable<
  paths['/admin/event-chat/participants/{eventId}/manual-artists']['post']['responses'][200]['content']
>['application/json'];
type UpdateAdminEventChatArtistAccessRequest = NonNullable<
  paths['/admin/event-chat/participants/{eventId}/artists/{artistId}/chat-access']['patch']['requestBody']
>['content']['application/json'];
type UpdateAdminEventChatArtistAccessResponse = NonNullable<
  paths['/admin/event-chat/participants/{eventId}/artists/{artistId}/chat-access']['patch']['responses'][200]['content']
>['application/json'];

function ensureOk(status: number, ok: boolean) {
  if (!ok) throw new ApiStatusError(status);
}

function ensureData<T>(data: T | undefined, status: number, ok: boolean): T {
  if (!ok || !data) throw new ApiStatusError(status);
  return data;
}

async function readOptionalJson(data: unknown, response: Response) {
  if (data && typeof data === 'object') return data as Record<string, unknown>;
  try {
    const json = await response.clone().json();
    return json && typeof json === 'object' ? (json as Record<string, unknown>) : {};
  } catch (_error) {
    return {};
  }
}

async function ensureOkWithPayloadMessage(response: Response, payload: Record<string, unknown>) {
  if (response.ok) return;
  const error = new Error(String(payload?.message || payload?.error || `HTTP ${response.status}`));
  (error as Error & { status?: number }).status = response.status;
  throw error;
}

export async function getAdminEventChatSidebarRow(
  eventId: string
): Promise<AdminEventChatSidebarRowResponse> {
  const { data, response } = await api.GET('/admin/event-chat/sidebar-row/{eventId}', {
    params: { path: { eventId } },
  });
  return ensureData(data as AdminEventChatSidebarRowResponse | undefined, response.status, response.ok);
}

export async function listEventChatMessages(
  eventId: string,
  offset: number,
  limit: number
): Promise<EventChatMessagesResponse> {
  const { data, response } = await api.GET('/event-chat/events/{eventId}/messages/{offset}/{limit}', {
    params: { path: { eventId, offset, limit } },
  });
  return ensureData(data as EventChatMessagesResponse | undefined, response.status, response.ok);
}

export async function getEventChatMessagesAround(
  eventId: string,
  messageId: string,
  before: number,
  after: number
): Promise<EventChatMessagesWindowResponse> {
  const { data, response } = await api.GET(
    '/event-chat/events/{eventId}/messages/around/{messageId}/{before}/{after}',
    {
      params: { path: { eventId, messageId, before, after } },
    }
  );
  return ensureData(data as EventChatMessagesWindowResponse | undefined, response.status, response.ok);
}

export async function listEventChatNewMessages(
  eventId: string,
  lastCreatedAt: string
): Promise<EventChatNewMessagesResponse> {
  const { data, response } = await api.GET('/event-chat/events/{eventId}/messages/new/{lastCreatedAt}', {
    params: { path: { eventId, lastCreatedAt } },
  });
  return ensureData(data as EventChatNewMessagesResponse | undefined, response.status, response.ok);
}

export async function getAdminEventChatUnreadCounts(): Promise<AdminEventChatUnreadCountsResponse> {
  const { data, response } = await api.GET('/event-chat/unread-counts/admin');
  return ensureData(data as AdminEventChatUnreadCountsResponse | undefined, response.status, response.ok);
}

export async function getArtistEventChatUnreadCounts(): Promise<ArtistEventChatUnreadCountsResponse> {
  const { data, response } = await api.GET('/event-chat/unread-counts/artist');
  return ensureData(data as ArtistEventChatUnreadCountsResponse | undefined, response.status, response.ok);
}

export async function listArtistEventChatAvailableEvents(
  offset: number
): Promise<ArtistEventChatAvailableEventsResponse> {
  const { data, response } = await api.GET('/artist/event-chat/available-events/{offset}', {
    params: { path: { offset } },
  });
  return ensureData(data as ArtistEventChatAvailableEventsResponse | undefined, response.status, response.ok);
}

export async function deleteEventChatMessage(
  eventId: string,
  messageId: string
): Promise<Record<string, unknown>> {
  const { data, response } = await api.DELETE('/event-chat/events/{eventId}/messages/{messageId}', {
    params: { path: { eventId, messageId } },
  });
  const payload = await readOptionalJson(data, response);
  await ensureOkWithPayloadMessage(response, payload);
  return payload;
}

export async function markEventChatMessagesRead(body: EventChatReadRequest) {
  const { response } = await api.POST('/event-chat/read', { body });
  ensureOk(response.status, response.ok);
}

export async function listAdminEventChatMessageReads(
  eventId: string,
  messageId: string
): Promise<AdminEventChatMessageReadsResponse> {
  const { data, response } = await api.GET('/admin/event-chat/events/{eventId}/reads/{messageId}', {
    params: { path: { eventId, messageId } },
  });
  const payload = await readOptionalJson(data, response);
  await ensureOkWithPayloadMessage(response, payload);
  return payload as AdminEventChatMessageReadsResponse;
}

export async function listAdminEventChatParticipants(
  eventId: string
): Promise<AdminEventChatParticipantsResponse | null> {
  const { data, response } = await api.GET('/admin/event-chat/participants/{eventId}', {
    params: { path: { eventId } },
  });
  if (!response.ok || !data) return null;
  return data as AdminEventChatParticipantsResponse;
}

export async function listArtistEventChatParticipants(
  eventId: string
): Promise<ArtistEventChatParticipantsResponse | null> {
  const { data, response } = await api.GET('/artist/event-chat/participants/{eventId}', {
    params: { path: { eventId } },
  });
  if (!response.ok || !data) return null;
  return data as ArtistEventChatParticipantsResponse;
}

async function ensureMultipartUploadOk(data: unknown, response: Response) {
  const payload = await readOptionalJson(data, response);
  await ensureOkWithPayloadMessage(response, payload);
  return payload;
}

export async function sendEventChatTextMessage(
  eventId: string,
  body: EventChatTextMessageRequest
) {
  const { data, response } = await api.POST('/events/{eventId}/messages/text', {
    params: { path: { eventId } },
    body,
  });
  return ensureMultipartUploadOk(data, response);
}

export async function sendEventChatImageMessages(
  eventId: string,
  body: EventChatImageUploadRequest
) {
  const { data, response } = await api.POST('/events/{eventId}/messages/images', {
    params: { path: { eventId } },
    body,
  });
  return ensureMultipartUploadOk(data, response);
}

export async function sendEventChatFileMessage(
  eventId: string,
  body: EventChatFileUploadRequest
) {
  const { data, response } = await api.POST('/events/{eventId}/messages/files', {
    params: { path: { eventId } },
    body,
  });
  return ensureMultipartUploadOk(data, response);
}

export async function getAdminEventChatNotifyMute(
  eventId: string
): Promise<AdminEventChatNotifyMuteResponse> {
  const { data, response } = await api.GET('/admin/event-chat/notify-mute/{eventId}', {
    params: { path: { eventId } },
  });
  return ensureData(data as AdminEventChatNotifyMuteResponse | undefined, response.status, response.ok);
}

export async function updateAdminEventChatNotifyMute(
  body: UpdateAdminEventChatNotifyMuteRequest
) {
  const { response } = await api.POST('/admin/event-chat/notify-mute', { body });
  ensureOk(response.status, response.ok);
}

export async function listCastingArtists(
  query: CastingArtistsQuery = {}
): Promise<CastingArtistsResponse> {
  const { data, response } = await api.GET('/admin/casting-artists', {
    params: { query },
  });
  return ensureData(data as CastingArtistsResponse | undefined, response.status, response.ok);
}

export async function addAdminEventChatManualArtist(
  eventId: string,
  body: AddAdminEventChatManualArtistsRequest
): Promise<AdminEventChatParticipantsSnapshotResponse> {
  const { data, response } = await api.POST('/admin/event-chat/participants/{eventId}/manual-artists', {
    params: { path: { eventId } },
    body,
  });
  const payload = await readOptionalJson(data, response);
  await ensureOkWithPayloadMessage(response, payload);
  return payload as AdminEventChatParticipantsSnapshotResponse;
}

export async function updateAdminEventChatArtistAccess(
  eventId: string,
  artistId: string,
  body: UpdateAdminEventChatArtistAccessRequest
): Promise<UpdateAdminEventChatArtistAccessResponse> {
  const { data, response } = await api.PATCH(
    '/admin/event-chat/participants/{eventId}/artists/{artistId}/chat-access',
    {
      params: { path: { eventId, artistId } },
      body,
    }
  );
  const payload = await readOptionalJson(data, response);
  await ensureOkWithPayloadMessage(response, payload);
  return payload as UpdateAdminEventChatArtistAccessResponse;
}
